import ManagerRepositories from '@/repository/manager';
import ShiftServiceRepositories from '@/repository/shift-service';
import FacilityStoreRepositories from '@/repository/facility-store';
import SpotBillingRepositories from '@/repository/spot-billing';
import instance from '@/repository/Repository';
import axios from 'axios';
import { setCsrfTokenHeader } from '@/util/csrf/csrf';
import { isSessionDestroyedError } from '@/util/auth/sessionDestroyed';

// manager
import { FacilityContextRepository } from '@/model/repository/manager/facilityContext/FacilityContextRepository';
import { AbsentUseKindRepository } from '@/model/repository/manager/absentUseKind/AbsentUseKindRepository';
import { AccountTransferAlertRepository } from '@/model/repository/manager/accountTransfer/AccountTransferAlertRepository';
import { AdministratorsRepository } from '@/model/repository/manager/administrators/AdministratorsRepository';
import { CompanyDrivenSettingRepository } from '~/model/repository/manager/attendance/CompanyDrivenSettingRepository';
import { AdministratorAttendancesRepository } from '@/model/repository/manager/administratorAttendances/AdministratorAttendancesRepository';
import { AgenciesRepository } from '@/model/repository/manager/agencies/AgenciesRepository';
import { AgreeToVideoStreamingTermsRepository } from '@/model/repository/manager/video/AgreeToVideoStreamingTermsRepository';
import { AuthRepository } from '@/model/repository/manager/auth/AuthRepository';
import { MembersRepository } from '@/model/repository/manager/members/MembersRepository';
import { MemberRegistrationRepository } from '@/model/repository/manager/members/MemberRegistrationRepository';
import { MeRepository } from '@/model/repository/manager/me/MeRepository';
import { WhereaboutsCheckSettingRepository } from '@/model/repository/manager/whereaboutsCheck/WhereaboutsCheckSettingRepository';
import { TopicsRepository } from '@/model/repository/manager/topics/TopicsRepository';
import { TopicInsidesRepository } from '@/model/repository/manager/topicInsides/TopicInsidesRepository';
import { PlansRepository } from '@/model/repository/manager/plans/PlansRepository';
import { SchedulesRepository } from '@/model/repository/manager/schedules/SchedulesRepository';
import { CommentsRepository } from '@/model/repository/manager/comments/CommentsRepository';
import { CommunicationBookDisplayAdministratorRepository } from '@/model/repository/manager/communicationBookDisplayAdministrator/CommunicationBookDisplayAdministratorRepository';
import { ServiceGroupsRepository } from '@/model/repository/manager/serviceGroups/ServiceGroupsRepository';
import { CategoriesRepository } from '@/model/repository/manager/categories/CategoriesRepository';
import { MealPatternsRepository } from '@/model/repository/manager/mealPatterns/MealPatternsRepository';
import { ServiceOptionsRepository } from '@/model/repository/manager/serviceOptions/ServiceOptionsRepository';
import { GroupsRepository } from '@/model/repository/manager/groups/GroupsRepository';
import { SleepingConditionRepository } from '@/model/repository/manager/dailyCheck/sleeping/condtion/repostiory';
import { HealthCheckRepository } from '@/model/repository/manager/dailyCheck/healthCheck/repository';
import { FacilityRepository } from '@/model/repository/manager/facility/FacilityRepository';
import { IAccidentReportRepository } from '@/model/repository/manager/accident/AccidentReportRepository';
import { BillConfigurationRepository } from '@/model/repository/manager/bill/BillConfigurationRepository';
import { BillsTotalRepository } from '@/model/repository/manager/bill/BillsTotalRepository';
import { SystemTroubleContactSettingRepository } from '@/model/repository/manager/communications/SystemTroubleContactSettingRepository';
import { PhotoRetailPriceRepository } from '@/model/repository/manager/parentSales/PhotoRetailPriceRepository';
import { FeatureRepository } from '@/model/repository/manager/feature/FeatureRepository';
import { ContactToScheduleApplyPreconditionRepository } from '@/model/repository/manager/parentContacts/ContactToScheduleApplyPreconditionRepository';
import { ICustomPlanReportsRepository } from '@/model/repository/manager/customPlanReports/CustomPlanReportsRepository';
import { IFacilityCapacityRepository } from '@/model/repository/manager/facility/FacilityCapacityRepository';
import { BetaFeatureRolloutRepository } from '@/model/repository/manager/facility/BetaFeatureRolloutRepository';
import { BillsConfirmRepository } from '@/model/repository/manager/bill/BillsConfirmRepository';
import { CommunicationNotebooksRepository } from '@/model/repository/manager/childcare-document/CommunicationNotebooksRepository';
import { ChildrenRepository } from '@/service/daily/getChild';
import { CurriculumsRepository } from '@/model/repository/manager/childcare-document/plan/CurriculumsRepository';
import { YearsRepository } from '@/model/repository/manager/childcare-document/plan/YearsRepository';
import { MonthliesRepository } from '@/model/repository/manager/childcare-document/plan/MonthliesRepository';
import { WeekliesRepository } from '@/model/repository/manager/childcare-document/plan/WeekliesRepository';
import { StatusesRepository } from '@/model/repository/manager/childcare-document/plan/StatusesRepository';

// shift
import { AbsenceCategoriesRepository } from '@/model/repository/shift/AbsenceCategoriesRepository';
import { BackdoorRepository } from '@/model/repository/shift/BackdoorRepository';
import { ChildClassesRepository } from '@/model/repository/shift/ChildClassesRepository';
import { ChildGroupsRepository } from '@/model/repository/shift/ChildGroupsRepository';
import { ClassGroupWorkplaceRepository } from '@/model/repository/shift/ClassGroupWorkplaceRepository';
import { ClassWorkplaceRepository } from '@/model/repository/shift/ClassWorkplaceRepository';
import { ShiftDatesRepository } from '@/model/repository/shift/ShiftDatesRepository';
import { DutiesRepository } from '@/model/repository/shift/DutiesRepository';
import { DutyOrdersRepository } from '@/model/repository/shift/DutyOrdersRepository';
import { GroupWorkplaceRepository } from '@/model/repository/shift/GroupWorkplaceRepository';
import { SettingsRepository } from '@/model/repository/shift/SettingsRepository';
import { ShiftCategoriesOrderRepository } from '@/model/repository/shift/ShiftCategoriesOrderRepository';
import { ShiftCategoriesRepository } from '@/model/repository/shift/ShiftCategoriesRepository';
import { ShiftPatternsOrderRepository } from '@/model/repository/shift/ShiftPatternsOrderRepository';
import { ShiftPatternsRepository } from '@/model/repository/shift/ShiftPatternsRepository';
import { ShiftCountRepository } from '@/model/repository/shift/ShiftCountRepository';
import { SimpleSettingsRepository } from '@/model/repository/shift/SimpleSettingsRepository';
import { StaffingLevelsRepository } from '@/model/repository/shift/StaffingLevelsRepository';
import { WorkplacesRepository } from '@/model/repository/shift/WorkplacesRepository';
import { RoleRepository } from '@/model/repository/manager/role/RoleRepository';
import { PhotoPriceSettingRulesRepository } from '@/model/repository/manager/parentSales/PhotoPriceSettingRulesRepository';
import { ErrorPresenter } from '@/repository/errorPresenter/types';

// facility-store
import { IExistUnprocessedChildOrdersRepository } from '@/model/repository/facility-store/IExistUnprocessedChildOrdersRepository';

// memories
import { PhotoBasePriceRepository } from '~/model/repository/manager/parentSales/PhotoBasePriceRepository';
import { TlnkOptionsRepository } from '~/model/repository/manager/tlnk/TlnkOptionsRepository';
import { MediaSelectorRepository } from '~/model/repository/manager/tlnk/MediaSelectorRepository';
import { MemoriesBetaFacilityRepository } from '~/model/repository/manager/parentSales/MemoriesBetaFacilityRepository';
import { SonomamaAlbumSettingsRepository } from '~/model/repository/manager/parentSales/SonomamaAlbumSettingsRepository';

// spot-billing
import { SpotBillingRepository } from '~/model/repository/spot-billing/SpotBillingRepository';
import { SleepingRemarkRepository } from '@/model/repository/manager/dailyCheck/sleeping/remark/repository';

const MANAGER_BASE_URL = location.origin;

const managerInstance = axios.create({
  withCredentials: true,
  baseURL: MANAGER_BASE_URL,
});

managerInstance.interceptors.request.use((request) => {
  request = setCsrfTokenHeader(request);
  try {
    request.params = request.params || {};
    // __env__をmgrで付与する
    request.params['__env__'] = 'mgr';
    request.headers['Codmon-Current-Page'] = window.location.href.split('#').pop();
  } catch (e) {
    request.headers['Codmon-Current-Page'] = '';
  }
  return request;
});

managerInstance.interceptors.response.use(
  (response) => {
    if (response.headers) {
      const csrfToken = response.headers['codmon-csrf-token'];
      try {
        csrfToken && localStorage.setItem('CODMON-CSRF-TOKEN', csrfToken);
      } catch (e) {
        window.browserLogger && window.browserLogger.error('CSRFトークンの保存に失敗しました: ', {}, e instanceof Error ? e : new Error(String(e)));
      }
    }
    return response;
  },
  (error) => {
    if (
      error &&
      error.response &&
      error.response.status === 401 &&
      error.response.data &&
      error.response.data.error &&
      error.response.data.error.exception === 'CSRFトークンエラーが発生しました'
    ) {
      location.href = '/manage/';
    }

    if (isSessionDestroyedError(error?.response?.status, error?.response?.data?.error?.exception)) {
      location.href = '/manage/';
    }

    return Promise.reject(error);
  }
);

const facilityStoreInstance = axios.create({
  withCredentials: true,
  baseURL: MANAGER_BASE_URL,
});

const spotBillingInstance = axios.create({
  withCredentials: true,
  baseURL: MANAGER_BASE_URL,
  timeout: 1000,
});

const managerRepositories = ManagerRepositories(managerInstance);
const shiftRepositories = ShiftServiceRepositories(instance);
const facilityStoreRepositories = FacilityStoreRepositories(facilityStoreInstance);
const spotBillingRepositories = SpotBillingRepositories(spotBillingInstance);

export interface ManagerRepositories {
  absentUseKind: AbsentUseKindRepository;
  accountTransferAlert: AccountTransferAlertRepository;
  administrators: AdministratorsRepository;
  companyDrivenSetting: CompanyDrivenSettingRepository;
  administratorAttendances: AdministratorAttendancesRepository;
  agencies: AgenciesRepository;
  agreeToVideoStreamingTerms: AgreeToVideoStreamingTermsRepository;
  auth: AuthRepository;
  categories: CategoriesRepository;
  mealPatterns: MealPatternsRepository;
  members: MembersRepository;
  memberRegistrationV4: MemberRegistrationRepository;
  me: MeRepository;
  whereaboutsCheckSetting: WhereaboutsCheckSettingRepository;
  topics: TopicsRepository;
  topicInsides: TopicInsidesRepository;
  plans: PlansRepository;
  curriculumsPlan: CurriculumsRepository;
  yearsPlan: YearsRepository;
  monthliesPlan: MonthliesRepository;
  weekliesPlan: WeekliesRepository;
  statuses: StatusesRepository;
  schedules: SchedulesRepository;
  comments: CommentsRepository;
  communicationBookDisplayAdministrator: CommunicationBookDisplayAdministratorRepository;
  serviceGroups: ServiceGroupsRepository;
  serviceOptions: ServiceOptionsRepository;
  groups: GroupsRepository;
  sleepingCondition: SleepingConditionRepository;
  sleepingRemark: SleepingRemarkRepository;
  healthCheck: HealthCheckRepository;
  roles: RoleRepository;
  rolesV4: RoleRepository;
  facility: FacilityRepository;
  facilityContext: FacilityContextRepository;
  accidentReport: IAccidentReportRepository;
  billConfiguration: BillConfigurationRepository;
  billsTotal: BillsTotalRepository;
  billsConfirm: BillsConfirmRepository;
  communicationNotebooks: CommunicationNotebooksRepository;
  systemTroubleContactSetting: SystemTroubleContactSettingRepository;
  photoRetailPrice: PhotoRetailPriceRepository;
  photoBasePrices: PhotoBasePriceRepository;
  photoPriceSettingRules: PhotoPriceSettingRulesRepository;
  feature: FeatureRepository;
  customPlanReports: ICustomPlanReportsRepository;
  // eslint-disable-next-line no-undef
  whereaboutsCheckResult: (errorPresenter: ErrorPresenter) => WhereaboutsCheckResultRepository;
  // eslint-disable-next-line no-undef
  whereaboutsCheckStatus: (errorPresenter: ErrorPresenter) => WhereaboutsCheckStatusRepository;
  facilityCapacity: IFacilityCapacityRepository;
  children: (errorPresenter: ErrorPresenter, isSupervisorMode: () => boolean) => ChildrenRepository;
  tlnkOptions: TlnkOptionsRepository;
  mediaSelector: MediaSelectorRepository;
  memoriesBetaFacility: MemoriesBetaFacilityRepository;
  sonomamaAlbumSettings: SonomamaAlbumSettingsRepository;
  contactToScheduleApplyPrecondition: ContactToScheduleApplyPreconditionRepository;
  betaFeatureRollout: BetaFeatureRolloutRepository;
}

export interface ShiftServiceRepositories {
  absenceCategories: AbsenceCategoriesRepository;
  backdoor: BackdoorRepository;
  childClasses: ChildClassesRepository;
  childGroups: ChildGroupsRepository;
  classGroupWorkplace: ClassGroupWorkplaceRepository;
  classWorkplace: ClassWorkplaceRepository;
  shiftDates: ShiftDatesRepository;
  duties: DutiesRepository;
  dutyOrders: DutyOrdersRepository;
  groupWorkplace: GroupWorkplaceRepository;
  settings: SettingsRepository;
  shiftCategories: ShiftCategoriesRepository;
  shiftCategoriesOrder: ShiftCategoriesOrderRepository;
  shiftPatterns: ShiftPatternsRepository;
  shiftCount: ShiftCountRepository;
  shiftPatternsOrders: ShiftPatternsOrderRepository;
  simpleSettings: SimpleSettingsRepository;
  staffingLevels: StaffingLevelsRepository;
  workplaces: WorkplacesRepository;
}

export interface FacilityStoreRepositories {
  existUnprocessedChildOrdersRepository: IExistUnprocessedChildOrdersRepository;
}

export interface SpotBillingRepositories {
  spotBillingRepository: SpotBillingRepository;
}

export interface ApiServices {
  manager: ManagerRepositories;
  shiftService: ShiftServiceRepositories;
  facilityStore: FacilityStoreRepositories;
  spotBilling: SpotBillingRepositories;
}

const services: ApiServices = {
  manager: managerRepositories,
  shiftService: shiftRepositories,
  facilityStore: facilityStoreRepositories,
  spotBilling: spotBillingRepositories,
};

export default services;
